// Every string PlanningWorksheet can render, and the shape a caller may replace
// them with.
//
// The mechanism is BidCard's exactly — one object of finished text in one
// language, merged over these defaults key by key, with an UNKNOWN key rejected
// because that is the failure that renders English forever with nothing saying
// why. The reasoning is written out once, in bidCardTranslations.js.
//
// What is worth saying here is why a form this size had four label props and
// twenty-odd fixed English strings beside them. The four were the ones somebody
// needed on the day; there was no principle separating them from the rest, and
// the rest included both halves of the margin verdict and the word that tells a
// dealer whether their plan was kept. Every one of those strings already existed
// in the application's own catalog, in both locales, bound to nothing — so the
// French worksheet was already written and no dealer had ever seen it.
//
// `referenceLabel` deliberately stays its own prop rather than moving in here.
// It is data that happens to be a word: which benchmark the page prices against
// changes when the asset changes, not when the language does, and folding it in
// would make the page rebuild this whole object every time the benchmark moved.
//
// There is no `formatMoney` member, which is the one place this differs from
// BidCard's `i18n`. Every figure in the worksheet goes through the library's
// shared `formatPrice`, which is en-US with a dollar sign for every component
// that draws money; giving one component its own money policy would be the first
// half of a library-wide decision taken in the wrong place. The object is closed,
// so passing one fails the validator rather than being ignored.
//
// Deliberately NOT exported from the package barrel: the catalog gate holds that
// a barrel exports components and nothing else.

import { unreadTranslationKeys as unreadKeysAgainst } from './bidCardTranslations';

export { interpolate } from './bidCardTranslations';

/**
 * The English defaults, frozen.
 *
 * `{name}` placeholders are filled by the component. A translation may move them
 * around its sentence; it may not invent one, since the worksheet only
 * substitutes the names it knows.
 */
export const PLANNING_WORKSHEET_TRANSLATIONS = Object.freeze({
  // ── The header ───────────────────────────────────────────────────────────
  // The worksheet's own name, and after this pass the form's heading.
  kicker: 'Planning worksheet',
  // The sentence under it, used only when the `hint` slot is empty. The app
  // fills the slot instead, because it emphasizes the liked list's name
  // inside the sentence and that is markup rather than text.
  hint: 'Your target bid and build-out for this asset. Saved to Liked.',

  // ── The target bid row ───────────────────────────────────────────────────
  // Also the summary's first row, because they are the same figure and two
  // words for it is how they end up disagreeing.
  targetBidLabel: 'Target bid',
  targetBidHint: "Max you'd pay at the block",

  // ── The build-out rows ───────────────────────────────────────────────────
  buildOutSectionLabel: 'Build-out / recon',
  // The name field's accessible name. It cannot be named by its own contents —
  // that is what the dealer is about to type — and a placeholder disappears at
  // the first keystroke, which is exactly when they most need to know where
  // they are.
  costItemNameLabel: 'Cost item name',
  costItemPlaceholder: 'Cost item',
  costAmountLabel: 'Cost',
  // One remove control per row, named for the row it takes. Four buttons all
  // called "Remove cost" tell a screen-reader user which button they are on and
  // nothing about which row leaves.
  removeCostLabel: 'Remove {label}',
  // The same control on a row nobody has named yet. `{position}` is the row's
  // place in the list, one-based, because a second unnamed row has nothing else
  // to tell it from the first.
  removeUnnamedCostLabel: 'Remove cost {position}',
  addCost: 'Add cost',
  customPreset: 'Custom…',

  // ── Notes ────────────────────────────────────────────────────────────────
  notesLabel: 'Notes',
  notesPlaceholder: 'Inspection notes, plan, follow-ups…',

  // ── The summary ──────────────────────────────────────────────────────────
  buildOutSummaryLabel: 'Build-out',
  // The auction's own cut, named rather than left out. The worksheet computes
  // no fee — see the component's `fees` prop — so this labels a figure the page
  // supplies, and labels the row as absent when it supplies none. A dealer who
  // cannot see the word reads the all-in as the whole cost of the car, which is
  // the one thing it is not.
  feesLabel: 'Fees',
  allInLabel: 'All-in',
  // The verdict, against whatever benchmark the page picked. Under is margin
  // and over is a car to walk away from, so they are two sentences rather than
  // one with a sign in front of it.
  marginUnder: '{amount} under',
  marginOver: '{amount} over',

  // ── The save control ─────────────────────────────────────────────────────
  saveToLiked: 'Save to Liked',
  saving: 'Saving…',
  // Read only when the PARENT says the save landed. The form used to say it the
  // instant it emitted, which made it a claim about a request rather than about
  // a result.
  saved: 'Saved',
  // The other half of that. A message rather than a button label, because what
  // the dealer can do next is still "save", and the button says so.
  saveFailed: 'Your plan was not saved. Try again.',

  // ── The preset chips ─────────────────────────────────────────────────────
  // The six kinds of work a dealer itemizes most. They are copy, not an enum:
  // a preset's only job is to fill a row's name field, and a row named in
  // French is as good a row as one named in English.
  presetBodyWork: 'Body work',
  presetGlass: 'Glass',
  presetDetailing: 'Detailing',
  presetTires: 'Tires',
  presetMechanical: 'Mechanical',
  presetPaint: 'Paint',
});

/**
 * The preset chips, in the order they are offered.
 *
 * Keys rather than words, so the order is the component's and the words are the
 * translation's. A preset already used as a row's label drops out of the offered
 * set, which is a comparison against the RENDERED name — so a worksheet stored
 * in one language and reopened in another offers the preset again. That is the
 * honest behavior: the row is named in the old language and nothing in it says
 * which preset it came from.
 */
export const PRESET_KEYS = Object.freeze([
  'presetBodyWork',
  'presetGlass',
  'presetDetailing',
  'presetTires',
  'presetMechanical',
  'presetPaint',
]);

/**
 * The keys in `translations` that PlanningWorksheet would not read, as dotted
 * paths.
 *
 * The walk itself is BidCard's — it already takes the defaults object as an
 * argument, so this surface passes its own rather than owning a second copy of a
 * comparison that has to behave identically on all three.
 */
export const unreadTranslationKeys = (translations) =>
  unreadKeysAgainst(translations, PLANNING_WORKSHEET_TRANSLATIONS);

/** One language's text over the English defaults, key by key. */
export function mergeTranslations(translations) {
  if (!translations) return PLANNING_WORKSHEET_TRANSLATIONS;

  return { ...PLANNING_WORKSHEET_TRANSLATIONS, ...translations };
}

/**
 * The `i18n` prop's validator: `{ translations }`, optional.
 *
 * Closed on the outer object too — `translation` for `translations` fails
 * exactly the way a mis-keyed string does, silently and in English.
 */
export function isValidPlanningWorksheetI18n(i18n) {
  if (i18n === null || typeof i18n !== 'object') return false;
  if (Object.keys(i18n).some((key) => key !== 'translations')) return false;
  if (i18n.translations === undefined) return true;
  return unreadTranslationKeys(i18n.translations).length === 0;
}
